import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { CalendarDays, CheckCircle2, RefreshCw, X, Download, AlertCircle } from "lucide-react";
import { formatDate } from "@/lib/dates";

const STATUS_PILL = {
  paid:       { cls: "pill-success", label: "Paid" },
  sent:       { cls: "pill-info",    label: "Sent to bank" },
  overdue:    { cls: "pill-danger",  label: "Overdue" },
  due_today:  { cls: "pill-warning", label: "Due today" },
  due_soon:   { cls: "pill-warning", label: "Due soon" },
  scheduled:  { cls: "pill-info",    label: "Scheduled" },
  cancelled:  { cls: "pill-info",    label: "Cancelled" },
};

function money(n) {
  if (n == null) return "—";
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dayDelta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "today";
  if (diff > 0) return `in ${diff} day${diff === 1 ? "" : "s"}`;
  return `${Math.abs(diff)} day${diff === -1 ? "" : "s"} ago`;
}

export default function EmployeePaymentsTab({ employeeId, viewerRole = "hr_admin", onSchedulingChange }) {
  const isHR = ["super_admin", "hr_admin"].includes(viewerRole);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [markOn, setMarkOn] = useState(null);
  const [regenOn, setRegenOn] = useState(false);

  const url = employeeId
    ? `/employees/${employeeId}/payment-schedule`
    : `/employees/me/payment-schedule`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load schedule");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [employeeId]);

  if (loading) return <div className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="payments-loading">Loading payment schedule…</div>;
  if (!data) return null;

  // Needs setup
  if (data.needs_setup) {
    return (
      <div className="cc-card p-6" data-testid="payments-needs-setup">
        <div className="flex items-center gap-3 mb-3">
          <AlertCircle className="w-5 h-5 text-[var(--cc-accent)]" />
          <h3 className="font-display text-lg font-bold">No payment schedule yet</h3>
        </div>
        <p className="text-sm text-[var(--cc-text-soft)] mb-4">
          {data.employee?.start_date
            ? <>The schedule will auto-generate from start date <strong>{formatDate(data.employee.start_date)}</strong>.</>
            : <>The employee has no <strong>start date</strong> set yet. HR Admin must set one in the Profile tab before a schedule can be generated.</>}
        </p>
        {isHR && data.employee?.start_date && (
          <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => setRegenOn(true)} data-testid="generate-schedule-button">
            <RefreshCw className="w-3.5 h-3.5" /> Generate schedule now
          </button>
        )}
        {regenOn && (
          <RegenerateModal
            employeeId={employeeId}
            employee={data.employee}
            annualSalary={null}
            onClose={() => setRegenOn(false)}
            onSaved={() => { setRegenOn(false); load(); onSchedulingChange?.(); }}
          />
        )}
      </div>
    );
  }

  const sched = data.schedule;
  const next = data.summary?.next_payment;
  const isHourly = !!sched.is_hourly_contract;

  return (
    <div className="space-y-5" data-testid="payments-tab-content">
      {/* Summary card */}
      <div className="cc-card p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="label-eyebrow mb-1">{isHourly ? "Contract" : "Annual salary"}</div>
          <div className="font-display text-2xl font-bold" data-testid="payments-annual-salary">
            {isHourly
              ? (sched.contract_type || "Hourly")
              : money(sched.annual_salary)}
          </div>
          {isHourly && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Hourly contract — pay per period is agreed with payroll.</div>}
        </div>
        <div>
          <div className="label-eyebrow mb-1">Pay frequency</div>
          <div className="font-display text-2xl font-bold">Every 4w</div>
          <div className="text-xs text-[var(--cc-text-soft)]">2-week arrears</div>
        </div>
        <div>
          <div className="label-eyebrow mb-1">Paid this year</div>
          <div className="font-display text-2xl font-bold" data-testid="payments-paid-count">{data.summary?.paid_periods}/{data.summary?.total_periods}</div>
        </div>
        <div>
          <div className="label-eyebrow mb-1">Bank account</div>
          <div className="font-display text-lg font-bold">{data.employee?.bank_account_last4 ? `••• ${data.employee.bank_account_last4}` : "Not set"}</div>
        </div>
      </div>

      {/* Next payment hero */}
      {next && (
        <div className="cc-card p-6 border-l-4 border-[var(--cc-sage)]" data-testid="next-payment-card">
          <div className="label-eyebrow mb-2">Next payment</div>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-3xl font-bold flex items-center gap-2">
                <CalendarDays className="w-6 h-6 text-[var(--cc-sage)]" />
                {formatDate(next.payment_due_date)}
              </div>
              <div className="text-sm text-[var(--cc-text-soft)] mt-1">
                Period {next.period_number} · Work: {formatDate(next.work_start)} → {formatDate(next.work_end)} · {dayDelta(next.payment_due_date)}
              </div>
            </div>
            {!isHourly && (
              <div className="text-right">
                <div className="label-eyebrow mb-1">Estimated</div>
                <div className="font-display text-2xl font-bold">{money(next.estimated_gross_pay)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HR actions */}
      {isHR && (
        <div className="flex justify-end">
          <button className="btn-secondary text-sm inline-flex items-center gap-1.5" onClick={() => setRegenOn(true)} data-testid="regenerate-schedule-button">
            <RefreshCw className="w-3.5 h-3.5" /> Regenerate from new start date
          </button>
        </div>
      )}

      {/* Full table */}
      <div className="cc-card overflow-hidden" data-testid="payments-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-2 label-eyebrow">#</th>
              <th className="px-4 py-2 label-eyebrow">Work period</th>
              <th className="px-4 py-2 label-eyebrow">Payment due</th>
              <th className="px-4 py-2 label-eyebrow">Estimated</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              {isHR && <th className="px-4 py-2 label-eyebrow">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sched.periods.map((p) => {
              const sp = STATUS_PILL[p.status] || STATUS_PILL.scheduled;
              const isPaid = p.paid_status === "paid";
              return (
                <tr key={p.id} className="border-t border-[var(--cc-border)]" data-testid={`payment-row-${p.period_number}`}>
                  <td className="px-4 py-2 font-mono text-xs">P{String(p.period_number).padStart(2, "0")}</td>
                  <td className="px-4 py-2 text-xs">{formatDate(p.work_start)} → {formatDate(p.work_end)}</td>
                  <td className="px-4 py-2 font-medium">{formatDate(p.payment_due_date)}</td>
                  <td className="px-4 py-2">{isHourly ? <span className="text-xs italic text-[var(--cc-text-soft)]">Hourly — agreed with payroll</span> : money(p.estimated_gross_pay)}</td>
                  <td className="px-4 py-2">
                    <span className={`pill ${sp.cls}`}>{sp.label}</span>
                    {isPaid && p.paid_at && <div className="text-[10px] text-[var(--cc-text-soft)] mt-1">on {formatDate(p.paid_at)}{p.paid_by_name ? ` by ${p.paid_by_name}` : ""}</div>}
                  </td>
                  {isHR && (
                    <td className="px-4 py-2">
                      {!isPaid && (
                        <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => setMarkOn(p)} data-testid={`mark-paid-${p.period_number}`} disabled={busy}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid
                        </button>
                      )}
                      {isPaid && (
                        <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={async () => {
                          if (!confirm("Revert this payment to scheduled?")) return;
                          setBusy(true);
                          try {
                            await api.post(`/employees/${employeeId}/payment-schedule/${p.id}/undo-paid`);
                            toast.success("Reverted to scheduled");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          finally { setBusy(false); }
                        }} data-testid={`undo-paid-${p.period_number}`}>
                          Undo
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {markOn && (
        <MarkPaidModal
          employeeId={employeeId}
          period={markOn}
          onClose={() => setMarkOn(null)}
          onSaved={() => { setMarkOn(null); load(); }}
        />
      )}
      {regenOn && (
        <RegenerateModal
          employeeId={employeeId}
          employee={data.employee}
          annualSalary={sched.annual_salary}
          onClose={() => setRegenOn(false)}
          onSaved={() => { setRegenOn(false); load(); onSchedulingChange?.(); }}
        />
      )}
    </div>
  );
}

function MarkPaidModal({ employeeId, period, onClose, onSaved }) {
  const [amount, setAmount] = useState(period.estimated_gross_pay ?? "");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/payment-schedule/mark-paid`, {
        period_id: period.id,
        amount_paid: amount === "" ? null : Number(amount),
        paid_at: paidAt,
        notes,
      });
      toast.success("Payment marked as paid");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} widthClass="max-w-md" testId="mark-paid-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">Mark payment as paid</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="cc-card p-3 mb-4 bg-[var(--cc-surface)] text-sm">
        Period {period.period_number}: <strong>{formatDate(period.work_start)}</strong> → <strong>{formatDate(period.work_end)}</strong><br/>
        Due <strong>{formatDate(period.payment_due_date)}</strong>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Amount paid (£)</label>
          <input type="number" step="0.01" className="cc-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="mark-paid-amount" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Paid on</label>
          <input type="date" className="cc-input" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="mark-paid-date" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Notes</label>
          <textarea className="cc-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mark-paid-notes" />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="mark-paid-submit">{busy ? "Saving…" : "Mark paid"}</button>
      </div>
    </Modal>
  );
}

function RegenerateModal({ employeeId, employee, annualSalary, onClose, onSaved }) {
  const [startDate, setStartDate] = useState((employee?.start_date || "").slice(0, 10));
  const [contractType, setContractType] = useState(employee?.contract_type || "permanent");
  const [salary, setSalary] = useState(annualSalary || "");
  const [busy, setBusy] = useState(false);
  const HOURLY = ["zero_hours", "bank", "relief", "variable_hours", "casual"];
  const isHourly = HOURLY.includes(contractType);
  const submit = async () => {
    if (!startDate) return toast.error("Start date is required");
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/payment-schedule/regenerate`, {
        start_date: startDate,
        contract_type: contractType,
        // For hourly contracts, never send a salary number
        annual_salary: isHourly ? null : (salary === "" ? null : Number(salary)),
      });
      toast.success("Payment schedule regenerated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} widthClass="max-w-md" testId="regenerate-schedule-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">Regenerate payment schedule</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-[var(--cc-text-soft)] mb-4">
        The previous schedule is archived (kept for audit). A fresh 13-period schedule is created anchored to the new start date.
      </p>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Start date *</label>
          <input type="date" className="cc-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="regen-start-date" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Contract type</label>
          <select className="cc-select" value={contractType} onChange={(e) => setContractType(e.target.value)} data-testid="regen-contract-type">
            <option value="permanent">Permanent (fixed annual salary)</option>
            <option value="fixed_term">Fixed-term (fixed salary)</option>
            <option value="zero_hours">Zero-hour</option>
            <option value="bank">Bank</option>
            <option value="relief">Relief</option>
            <option value="variable_hours">Variable hours</option>
            <option value="casual">Casual</option>
          </select>
        </div>
        {!isHourly && (
          <div>
            <label className="label-eyebrow block mb-1">Annual salary (£)</label>
            <input type="number" step="0.01" className="cc-input" value={salary} onChange={(e) => setSalary(e.target.value)} data-testid="regen-salary" />
          </div>
        )}
        {isHourly && (
          <div className="cc-card p-3 bg-[var(--cc-surface)] text-xs text-[var(--cc-text-soft)]">
            No salary required — pay for each 4-week period is agreed with payroll using the hourly rate on file.
          </div>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="regen-submit">{busy ? "Regenerating…" : "Regenerate"}</button>
      </div>
    </Modal>
  );
}
